import React, { useContext, useEffect, useState } from 'react';
import { SitecoreContext, Field, ImageField, RichText } from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PreviewStandardImage, {
  PreviewStandardImageDetails,
} from '../PreviewStandardImage/PreviewStandardImage';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { deleteAPICall, getAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import { QuantityFieldsProps } from '../Quantity/Quantity';
import { addEllipsis, getCurrencyInUSD } from '../../utils/helper';
import RichToastNotification, {
  RichToastMesssageProps,
} from '../RichToastNotification/RichToastNotification';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import nextId from 'react-id-generator';
import { getIdpToken } from '../../utils/authHelper';
import { pushUnifiedViewCart } from '../../utils/analyticsHelper';
import { ICP_IDENTIFIER, LOB_DISPLAY_NAMES, LOB_TITLES } from '../../utils/constants';
import { getUnifiedCartItems, shouldPushUnifiedViewCart } from '../../utils/unifiedCartHelper';
import { LOB_LIST } from '../../context/reducer';

export type ICPProductCartDetailsProps = {
  fields: {
    CartEmptyText: Field<string>;
    ThumbnailImage: ImageField;
    ThumbnailText: Field<string>;
    confirmationMessage: Field<string>;
    okButtonText: Field<string>;
    cancelButtonText: Field<string>;
    ToastMessageForRemoveItem: Field<string>;
    PreviewBaseUrl: Field<string>;
    ThumbnailBaseUrl: Field<string>;
    ExamWindowTitle: Field<string>;
    InvoiceNumberTitle: Field<string>;
    LateFeeTitle: Field<string>;
    UnitPriceTitle: Field<string>;
    PaymentPastDeadlineMessage: Field<string>;
    GenericFailureMessage: Field<string>;
  };

  previewStdFields: PreviewStandardImageDetails;
  quantityFields: QuantityFieldsProps;
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

export interface ICPCartProductFieldsProps {
  title: string;
  productId: string;
  publicationNbr: string;
  edition: number;
  publishedDate: string;
  previewUrl: string;
  thumbnailUrl: string;
  status: string;
  isRedlineExist: boolean;
  isRedline: boolean;
  isRecent: boolean;
  isDatasheet: boolean;
  productType: string;
  publicationType: string;
  term: string;
  year: number;
  isStatisticsReport: boolean;
  apiPoNumber: string;
  examWindow: string;
  isSurchargeApplied: boolean;
  surchargeType: string;
  surchargeCost: number;
  isSurchargeWaivedOff: boolean;
  surchargeLineItemId: string;
  isExamWindowAvailable: boolean;
  applicationNumber: string;
}

export interface ICPCartPriceOptionFieldsProps {
  format: string;
  isActive: boolean;
  language: string;
  quantity: number;
  unitPrice: number;
  variantId: string;
}

export interface ICPCartDetails {
  orderId: string;
  quantity: number;
  items: Array<ICPCartItemsDetails>;
  LOB: string;
}

export type ICPCartItemsDetails = {
  lineItemId: string;
  product: ICPCartProductFieldsProps;
  priceOption: ICPCartPriceOptionFieldsProps;
  sendToEmailAddresses: Array<string>;
  isValid: boolean;
  message: string;
  dateAdded?: string;
};

export const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
  root: {
    minWidth: 200,
  },
}));

export const ICPProductCartDetails = ({ fields }: ICPProductCartDetailsProps): JSX.Element => {
  const classes = useStyles();
  const [cartItemDetails, setCartItemDetails] = useState<any[]>([]);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessages, setToastMessages] = useState<Array<RichToastMesssageProps>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [lineItemId, setLineItemId] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [item, setItem] = useState<ICPCartItemsDetails>();

  const toastRemove = () => {
    setShowToast(false);
  };

  const quantityUpdated = () => {
    fetchData();

    if (dispatch) {
      dispatch({
        type: 'CART_UPDATE',
        payload: {
          toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
          toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad,
        },
      });
    }
  };

  function getProductCartSkeleton() {
    return (
      <Grid className="productCartGrid" item xs={9} sm={6} md={3}>
        <Card className={classes.root} variant="outlined">
          <CardContent>
            <Typography className="title" color="textSecondary" gutterBottom>
              <LoadingSkeleton theme="dark" />
            </Typography>

            <div className="cartDetailGrid">
              <div className="thumbnailsection">
                <LoadingSkeleton theme="light" loaderHeight="130px" loaderWidth="110px" />
              </div>
              <div className="productdetailssection">
                <div className="productInfoSection">
                  <div className="editionSection">
                    <p className="productInfoFieldTitle">{fields?.InvoiceNumberTitle?.value}:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                </div>
                <div className="productInfoSection">
                  <div className="languageSection">
                    <p className="productInfoFieldTitle">Language:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                  <div className="statusSection">
                    <p className="productInfoFieldTitle">{fields.ExamWindowTitle.value}:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                </div>
              </div>
              <div className="icppricedetailssection">
                <p className="priceTitle">Unit Price</p>
                <p className="unitPrice">
                  <LoadingSkeleton theme="light" loaderHeight="30px" loaderWidth="100px" />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  function renderRichTextMessage(richTextTemplate?: Field<string>, message?: string) {
    if (!richTextTemplate?.value || !message) return null;

    const replacedHtml = richTextTemplate.value.replace('##MESSAGE##', message);
    const sanitizedHtml = DOMPurify.sanitize(replacedHtml);

    return (
      <div className="notificationDetailText" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    );
  }

  function getPaymentPastDeadlineURL(
    paymentPastDeadlineMessage: Field<string>,
    applicationId: string
  ) {
    const updatedRichText = paymentPastDeadlineMessage.value.replace(
      '##APPLICATION_ID##',
      `?applicationId=${applicationId}`
    );

    return <div dangerouslySetInnerHTML={{ __html: updatedRichText }} />;
  }

  const fetchData = async () => {
    const idpToken = getIdpToken();
    let hasSyncFailure = false;

    try {
      const unifiedCartItems = await getUnifiedCartItems(idpToken, state?.unifiedCartItems);
      const icpCart = unifiedCartItems[ICP_IDENTIFIER];
      const items = icpCart?.items ?? [];

      items.forEach((item: any) => {
        if (item.isValid === false) {
          hasSyncFailure = true;
        }
      });

      const syncFailedCartLOBs = state?.syncFailedCartLOBs || [];

      const updatedSyncFailedCartLOBs = hasSyncFailure
        ? [...new Set([...syncFailedCartLOBs, ICP_IDENTIFIER])]
        : syncFailedCartLOBs.filter((lob) => lob !== ICP_IDENTIFIER);

      setCartItemDetails(items);
      setIsLoading(false);

      dispatch?.({
        type: 'CART_UPDATE',
        payload: {
          toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
          toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad,
          syncFailedCartLOBs: updatedSyncFailedCartLOBs,
          unifiedCartItems,
        },
      });

      if (shouldPushUnifiedViewCart()) {
        pushUnifiedViewCart(
          LOB_LIST.map((lob) => ({
            lob,
            items: unifiedCartItems[lob]?.items ?? [],
          })),
          LOB_DISPLAY_NAMES
        );
      }
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (state?.cartCounts?.lobCounts?.ICP ?? 0 > 0) {
      fetchData();
    }
  }, [state?.cartCounts?.lobCounts?.ICP]);

  const handleOpen = (item: any) => {
    setOpen(true);
    setLineItemId(item.lineItemId);
    setOrderId(item.orderId);
    setItem(item);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const removeAPICall = async (orderId: string, lineItemId: string) => {
    const idpToken = getIdpToken();

    try {
      const response = await deleteAPICall(
        `${process.env.REACT_APP_CART_URL}/CartOrder/${orderId}/delete`,
        {
          headers: {
            'B2C-Authorization': idpToken,
          },
        }
      );
      if (response && response.status === 200) {
        return response;
      } else {
        throw new Error('Failed to remove item');
      }
    } catch (error) {
      throw error;
    }
  };

  async function removeLineItem() {
    try {
      // Remove the main product line item
      await removeAPICall(orderId, lineItemId);
      if (item) {
        removeItem(item);
      }

      quantityUpdated();
    } catch (error) {
      console.error('Error removing line item:', error);
    }
  }

  function handleCloseWithOK() {
    setOpen(false);
    removeLineItem();
  }

  const removeItem = (cartItem: ICPCartItemsDetails) => {
    setCartItemDetails(cartItemDetails.filter((x) => x.lineItemId !== cartItem.lineItemId));

    if (toastMessages) {
      toastMessages.splice(0, toastMessages.length);
    }

    let messages = fields?.ToastMessageForRemoveItem.value.split('##').filter(Boolean);
    messages.map((item) => {
      if (item === 'PRODUCT_TITLE') {
        toastMessages.push({
          type: 'text',
          message: cartItem.product.title,
        });
      } else {
        toastMessages.push({
          type: 'text',
          message: item,
        });
      }
    });

    setToastMessages([...toastMessages]);
    setShowToast(true);
  };

  function getICPProductDetailsSection(item: ICPCartItemsDetails) {
    let errorMessage = state?.sitecoreDictionaryData?.[item.message];
    const isValid = item.isValid;

    function renderRichText() {
      if (errorMessage && isValid === false) {
        const examWindowMessage = state?.sitecoreDictionaryData?.ExamWindowExpirationMessage;

        if (item.message === examWindowMessage) {
          return getPaymentPastDeadlineURL(
            fields?.PaymentPastDeadlineMessage,
            item.product?.applicationNumber
          );
        }

        return renderRichTextMessage(fields?.GenericFailureMessage, errorMessage);
      } else if (isValid === false) {
        const genericFailureMessage =
          state?.sitecoreDictionaryData?.GenericErrorMessage.replace(
            '###LOB###',
            LOB_TITLES[ICP_IDENTIFIER]
          ) ||
          'The item is no longer valid. Please remove from the cart and add it from ICP again if needed.';

        return renderRichTextMessage(fields?.GenericFailureMessage, genericFailureMessage);
      }

      return null;
    }
    const showInvoiceNumber =
      (state?.cartCounts?.lobCounts?.ICP ?? 0) === 1 && state?.cartCounts?.totalCount === 1;

    return (
      <>
        <Grid
          container
          className={`icpproductdetailssection ${!isValid ? 'minimizedGrid' : ''}`}
          spacing={1}
          key={nextId()}
        >
          {showInvoiceNumber && (
            <Grid item xs={6}>
              <div className="editionSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  {fields?.InvoiceNumberTitle?.value}:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {item.product?.apiPoNumber}
                </p>
              </div>
            </Grid>
          )}

          {item.priceOption?.language && (
            <Grid item xs={6}>
              <div className="languageSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  Language:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {item.priceOption.language}
                </p>
              </div>
            </Grid>
          )}

          {item.product?.examWindow && (
            <Grid item xs={6}>
              <div className="statusSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  {fields.ExamWindowTitle.value}:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {item.product.examWindow}
                </p>
              </div>
            </Grid>
          )}
        </Grid>

        <div className={`icppricedetailssection ${!isValid ? 'maximizedGrid' : ''}`} key={nextId()}>
          {isValid ? (
            <>
              <div className="ICPPriceDetails">
                <p className="priceTitle" key={nextId()}>
                  {fields?.UnitPriceTitle.value}:
                </p>
                <p className="unitPrice" key={nextId()}>
                  {getCurrencyInUSD(item.priceOption?.unitPrice)}
                </p>
              </div>

              {item.product?.isSurchargeApplied && !item.product?.isSurchargeWaivedOff && (
                <div className="ICPPriceDetails">
                  <p className="priceTitle" key={nextId()}>
                    {item?.product?.surchargeType}:
                  </p>
                  <p className="surcharge" key={nextId()}>
                    {getCurrencyInUSD(item.product?.surchargeCost)}
                  </p>
                </div>
              )}
            </>
          ) : (
            renderRichText()
          )}
        </div>
      </>
    );
  }

  return (
    <div>
      <RichToastNotification
        key="richToast"
        toastMessages={toastMessages}
        showToast={showToast}
        removeToast={toastRemove}
      ></RichToastNotification>
      <div className="icpCartDetailsSection">
        {(state?.cartCounts?.lobCounts?.ICP ?? 0) > 0 ? (
          isLoading ? (
            getProductCartSkeleton()
          ) : (
            <Grid
              container
              direction="column"
              justifyContent="space-around"
              alignItems="stretch"
              className="icpGridMain"
              key={nextId()}
            >
              {cartItemDetails?.map((item: ICPCartItemsDetails, id: number) => {
                return (
                  <Grid className="productCartGrid" item xs={9} sm={6} md={3} key={nextId()}>
                    <Card className={classes.root} variant="outlined" key={nextId()}>
                      <CardContent key={nextId()}>
                        <Typography
                          className="title"
                          color="textSecondary"
                          gutterBottom
                          key={nextId()}
                        >
                          <div className="header-title-area">
                            <div className="product-title">
                              <span className="title-name">{item.product?.title}</span>
                              {addEllipsis(item.product?.title)}
                              {item.isValid === false &&
                                item.message ===
                                  state?.sitecoreDictionaryData?.SyncOrderErrorMessage && (
                                  <Tooltip
                                    title={
                                      state?.sitecoreDictionaryData?.SyncOrderFailureMessage.replace(
                                        '##LOB##',
                                        ICP_IDENTIFIER
                                      ) ||
                                      'It looks like the ICP order may have changed. Please remove and re-add the items to proceed.'
                                    }
                                  >
                                    <SyncProblemIcon className="syncIcon" />
                                  </Tooltip>
                                )}
                            </div>
                            <Link to="#" onClick={() => handleOpen(item)} className="remove-link">
                              Remove
                            </Link>
                          </div>
                        </Typography>
                        <Dialog open={open} onClose={handleClose}>
                          <DialogTitle id="alert-dialog-title">
                            {fields.confirmationMessage?.value}
                          </DialogTitle>
                          <DialogActions>
                            <button className="okButton" onClick={handleCloseWithOK}>
                              {fields.okButtonText?.value}
                            </button>
                            <button className="cancelButton" onClick={handleClose} autoFocus>
                              {fields.cancelButtonText?.value}
                            </button>
                          </DialogActions>
                        </Dialog>
                        <div className="cartDetailGrid" key={nextId()}>
                          <div className="thumbnail-main">
                            <div className="thumbnailsection" key={nextId()}>
                              <PreviewStandardImage
                                previewStdFields={{
                                  previewUrl: item.product?.previewUrl,
                                  thumbnailUrl: item.product?.thumbnailUrl,
                                  isDatasheet: item.product?.isDatasheet,
                                }}
                                fields={{
                                  ThumbnailImage: fields.ThumbnailImage,
                                }}
                                PreviewBaseUrl={fields?.PreviewBaseUrl}
                                ThumbnailBaseUrl={fields?.ThumbnailBaseUrl}
                              ></PreviewStandardImage>
                            </div>
                            <p className="thumbnailTextArea">{fields?.ThumbnailText?.value}</p>
                          </div>
                          {getICPProductDetailsSection(item)}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (
          <div className="empty-cart">
            <RichText field={fields.CartEmptyText} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ICPProductCartDetails;
